/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";

/** @module react/utils/anchor-wrapper */
import React from 'react';
import resetClassNames from '../css/reset.module.scss';
import { mergeClassNames } from '../modules/merge-class-names.js';
import { mergeStyles } from '../modules/merge-styles.js';

/**
 * @typedef anchorWrapperPropsType
 * @property {*} [children=null] - React prop.
 * @property {(string|null)} [href=null] - Component href.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @description A component for wrapping elements with an anchor element.
 * @param {anchorWrapperPropsType} props - Props.
 * @returns {Object<string,*>} React component.
 */
function AnchorWrapper(props) {
  var children = props.children,
      href = props.href,
      style = props.style,
      className = props.className,
      customStyles = props.customStyles,
      customClassNames = props.customClassNames,
      globalTheme = props.globalTheme,
      other = _objectWithoutProperties(props, ["children", "href", "style", "className", "customStyles", "customClassNames", "globalTheme"]);

  var globalStyles = globalTheme ? AnchorWrapper.globalStyles[globalTheme] || {} : {};
  var globalClassNames = globalTheme ? AnchorWrapper.globalClassNames[globalTheme] || {} : {};
  var mergedStyles = {
    root: mergeStyles({
      cursor: 'pointer'
    }, globalStyles.root, customStyles.root, style)
  };
  var mergedClassNames = {
    root: mergeClassNames(resetClassNames.root, globalClassNames.root, customClassNames.root, className)
  };
  return href ? /*#__PURE__*/React.createElement("a", _extends({}, other, {
    href: href,
    target: '_blank',
    rel: 'noopener noreferrer',
    style: mergedStyles.root,
    className: mergedClassNames.root
  }), children) : children;
}

AnchorWrapper.defaultProps = {
  children: null,
  href: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
};
AnchorWrapper.globalStyles = {
  default: {
    root: {}
  }
};
AnchorWrapper.globalClassNames = {
  default: {
    root: ''
  }
};
export { AnchorWrapper };